/**
 * Asset Manager - Centralized asset loading and caching
 * 
 * Features:
 * - GLB/GLTF model loading with animations
 * - Texture loading and management
 * - Asset caching and reuse
 * - Progress tracking
 * - Asset catalog support
 * 
 * Reference: https://doc.babylonjs.com/features/featuresDeepDive/importers/loadingFileTypes
 */

import { Scene } from "@babylonjs/core/scene";
import { SceneLoader } from "@babylonjs/core/Loading/sceneLoader";
import { TransformNode } from "@babylonjs/core/Meshes/transformNode";
import { AbstractMesh } from "@babylonjs/core/Meshes/abstractMesh";
import { AnimationGroup } from "@babylonjs/core/Animations/animationGroup";
import { Skeleton } from "@babylonjs/core/Bones/skeleton";
import { Texture } from "@babylonjs/core/Materials/Textures/texture";
import { Vector3 } from "@babylonjs/core/Maths/math.vector";

// Import GLTF loader - required for GLB support
import "@babylonjs/loaders/glTF";

import { IDisposable } from "../core/Engine.types";
import {
    ModelAssetDefinition,
    TextureAssetDefinition,
    LoadedModelAsset,
    LoadedTextureAsset,
    AssetLoadProgressCallback,
    AssetCatalog,
} from "./Asset.types";

/**
 * Cached model data (template for instancing)
 */
interface CachedModel {
    meshes: AbstractMesh[];
    animationGroups: AnimationGroup[];
    skeleton: Skeleton | null;
    definition: ModelAssetDefinition;
    instanceCount: number;
}

/**
 * AssetManager - Handles all asset loading and caching
 */
export class AssetManager implements IDisposable {
    private _scene: Scene;
    private _basePath: string;
    private _modelCache: Map<string, CachedModel> = new Map();
    private _textureCache: Map<string, Texture> = new Map();
    private _loadingAssets: Map<string, Promise<unknown>> = new Map();
    private _catalog: AssetCatalog | null = null;

    /**
     * Get the number of cached models
     */
    public get cachedModelCount(): number {
        return this._modelCache.size;
    }

    /**
     * Get the number of cached textures
     */
    public get cachedTextureCount(): number {
        return this._textureCache.size;
    }

    constructor(scene: Scene, basePath: string = "assets/") {
        this._scene = scene;
        this._basePath = basePath;
    }

    /**
     * Set the asset catalog for predefined asset definitions
     */
    public setCatalog(catalog: AssetCatalog): void {
        this._catalog = catalog;
    }

    /**
     * Load a model from catalog by ID
     */
    public async loadModelFromCatalog(id: string): Promise<LoadedModelAsset> {
        if (!this._catalog || !this._catalog.models[id]) {
            throw new Error(`Model "${id}" not found in catalog`);
        }
        return this.loadModel(this._catalog.models[id]);
    }

    /**
     * Load a GLB/GLTF model
     * 
     * @param definition - Model asset definition
     * @returns Loaded model with meshes, animations, and skeleton
     */
    public async loadModel(definition: ModelAssetDefinition): Promise<LoadedModelAsset> {
        const cacheKey = definition.id;

        // Check if already loading
        if (this._loadingAssets.has(cacheKey)) {
            await this._loadingAssets.get(cacheKey);
        }

        // Return cached instance if available
        if (this._modelCache.has(cacheKey)) {
            return this._createModelInstance(cacheKey);
        }

        // Start loading
        const loadPromise = this._loadModelInternal(definition);
        this._loadingAssets.set(cacheKey, loadPromise);

        try {
            await loadPromise;
            return this._createModelInstance(cacheKey);
        } finally {
            this._loadingAssets.delete(cacheKey);
        }
    }

    /**
     * Internal model loading
     */
    private async _loadModelInternal(definition: ModelAssetDefinition): Promise<void> {
        const fullPath = this._basePath + definition.path;
        const directory = fullPath.substring(0, fullPath.lastIndexOf("/") + 1);
        const filename = fullPath.substring(fullPath.lastIndexOf("/") + 1);

        console.log(`AssetManager: Loading model "${definition.id}" from ${fullPath}`);

        const result = await SceneLoader.ImportMeshAsync(
            "", // Load all meshes
            directory,
            filename,
            this._scene
        );

        // Note: Scale is applied to rootNode in _createModelInstance, not here
        // This keeps the original mesh data intact for proper physics/bounds calculation

        // Hide loaded meshes (they're templates until instanced)
        result.meshes.forEach(mesh => {
            mesh.setEnabled(false);
        });

        // Stop all animations initially
        result.animationGroups.forEach(ag => {
            ag.stop();
        });

        // Cache the model
        this._modelCache.set(definition.id, {
            meshes: result.meshes,
            animationGroups: result.animationGroups,
            skeleton: result.skeletons.length > 0 ? result.skeletons[0] : null,
            definition,
            instanceCount: 0,
        });

        console.log(`AssetManager: Model "${definition.id}" loaded with ${result.meshes.length} meshes, ${result.animationGroups.length} animations`);
        if (result.animationGroups.length > 0) {
            console.log(`AssetManager: Available animations:`, result.animationGroups.map(ag => ag.name));
        }
    }

    /**
     * Create an instance of a cached model
     * For the first instance, we use the original meshes/skeleton/animations directly
     * For subsequent instances, we clone them
     */
    private _createModelInstance(cacheKey: string): LoadedModelAsset {
        const cached = this._modelCache.get(cacheKey);
        if (!cached) {
            throw new Error(`Model "${cacheKey}" not in cache`);
        }

        cached.instanceCount++;
        const isFirstInstance = cached.instanceCount === 1;

        // Create root node for this instance
        const rootNode = new TransformNode(`${cached.definition.id}_instance_${cached.instanceCount}`, this._scene);
        
        // Apply scale from definition to the root node
        if (cached.definition.scale && cached.definition.scale !== 1) {
            const scale = cached.definition.scale;
            rootNode.scaling = new Vector3(scale, scale, scale);
        }

        let meshes: AbstractMesh[];
        let skeleton: Skeleton | null;
        let animationGroups: AnimationGroup[];

        if (isFirstInstance) {
            // First instance - use original assets directly (more reliable for animations)
            meshes = cached.meshes;
            skeleton = cached.skeleton;
            animationGroups = cached.animationGroups;

            // Parent meshes to root node and enable them
            meshes.forEach(mesh => {
                if (!mesh.parent || mesh.parent.name === "") {
                    mesh.parent = rootNode;
                }
                mesh.setEnabled(true);
            });

        } else {
            // Clone for additional instances
            meshes = [];
            cached.meshes.forEach(mesh => {
                const clone = mesh.clone(`${mesh.name}_${cached.instanceCount}`, null);
                if (clone) {
                    if (!mesh.parent || mesh.parent.name === "") {
                        clone.parent = rootNode;
                    }
                    clone.setEnabled(true);
                    meshes.push(clone);
                }
            });

            // Clone skeleton
            skeleton = null;
            if (cached.skeleton) {
                skeleton = cached.skeleton.clone(`${cached.definition.id}_skeleton_${cached.instanceCount}`);
                meshes.forEach(mesh => {
                    if (mesh.skeleton) {
                        mesh.skeleton = skeleton!;
                    }
                });
            }

            // Clone animations with proper retargeting
            animationGroups = [];
            cached.animationGroups.forEach(ag => {
                const clonedAg = ag.clone(`${ag.name}_${cached.instanceCount}`, (oldTarget) => {
                    if (skeleton && oldTarget.name) {
                        const bone = skeleton.bones.find(b => b.name === oldTarget.name);
                        if (bone) return bone;
                    }
                    // Find corresponding mesh
                    const originalIndex = cached.meshes.findIndex(m => m === oldTarget || m.name === oldTarget.name);
                    if (originalIndex >= 0 && meshes[originalIndex]) {
                        return meshes[originalIndex];
                    }
                    return oldTarget;
                });
                if (clonedAg) {
                    animationGroups.push(clonedAg);
                }
            });
        }

        return {
            rootNode,
            meshes,
            animationGroups,
            skeleton,
            definition: cached.definition,
        };
    }

    /**
     * Load a texture
     */
    public async loadTexture(definition: TextureAssetDefinition): Promise<LoadedTextureAsset> {
        const cacheKey = definition.id;

        if (this._textureCache.has(cacheKey)) {
            return {
                texture: this._textureCache.get(cacheKey)!,
                definition,
            };
        }

        const fullPath = this._basePath + definition.path;
        
        return new Promise((resolve, reject) => {
            const texture = new Texture(
                fullPath,
                this._scene,
                !definition.generateMipMaps, // noMipmap
                false, // invertY
                Texture.TRILINEAR_SAMPLINGMODE,
                () => {
                    this._textureCache.set(cacheKey, texture);
                    resolve({ texture, definition });
                },
                (message, _exception) => {
                    reject(new Error(`Failed to load texture: ${message}`));
                }
            );
        });
    }

    /**
     * Preload multiple assets with progress tracking
     */
    public async preloadAssets(
        modelIds: string[],
        textureIds: string[],
        onProgress?: AssetLoadProgressCallback
    ): Promise<void> {
        if (!this._catalog) {
            throw new Error("No asset catalog set. Call setCatalog() first.");
        }

        const totalAssets = modelIds.length + textureIds.length;
        let loadedCount = 0;

        const updateProgress = (currentAsset: string) => {
            loadedCount++;
            onProgress?.(loadedCount, totalAssets, currentAsset);
        };

        // Load models
        for (const id of modelIds) {
            await this.loadModelFromCatalog(id);
            updateProgress(id);
        }

        // Load textures
        for (const id of textureIds) {
            if (this._catalog.textures[id]) {
                await this.loadTexture(this._catalog.textures[id]);
                updateProgress(id);
            }
        }
    }

    /**
     * Check if a model is cached
     */
    public isModelCached(id: string): boolean {
        return this._modelCache.has(id);
    }

    /**
     * Check if a texture is cached
     */
    public isTextureCached(id: string): boolean {
        return this._textureCache.has(id);
    }

    /**
     * Clear a specific model from cache
     */
    public clearModel(id: string): void {
        const cached = this._modelCache.get(id);
        if (cached) {
            cached.meshes.forEach(mesh => mesh.dispose());
            cached.animationGroups.forEach(ag => ag.dispose());
            cached.skeleton?.dispose();
            this._modelCache.delete(id);
        }
    }

    /**
     * Clear all cached assets
     */
    public clearCache(): void {
        // Dispose models
        this._modelCache.forEach((cached) => {
            cached.meshes.forEach(mesh => mesh.dispose());
            cached.animationGroups.forEach(ag => ag.dispose());
            cached.skeleton?.dispose();
        });
        this._modelCache.clear();

        // Dispose textures
        this._textureCache.forEach(texture => texture.dispose());
        this._textureCache.clear();
    }

    /**
     * Dispose the asset manager
     */
    public dispose(): void {
        this.clearCache();
    }
}
